// Data layer for the v2 planner. Talks to the SAME backend as v1:
//   GET   /:user/plans/:slug/data  -> Plan#data (version-specific JSON)
//   PATCH /:user/plans/:slug       -> save (plan[content] = v3)
// Migrates whatever the server returns up to v3 in memory so older plans render.

import { migrate } from './migrations';

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

export async function loadPlanData(dataUrl) {
  const response = await fetch(dataUrl, {
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
  });
  if (!response.ok) throw new Error(`Failed to load plan (${response.status})`);
  return migrate(await response.json());
}

// Save via fetch (not p5.httpDo) so we can read validation errors — mirrors the
// pattern shipped for v1 in PR #88. Resolves to the parsed JSON on success;
// throws an Error whose message is the server's reason on failure.
export async function savePlan(saveUrl, payload) {
  const response = await fetch(saveUrl, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let message = 'Something went wrong while saving. Please try again.';
    try {
      const errors = await response.json();
      const parts = Object.entries(errors).map(([field, msgs]) => {
        const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
        return field === 'base' ? text : `${field} ${text}`;
      });
      if (parts.length) message = parts.join('. ');
    } catch (_) { /* non-JSON error body */ }
    throw new Error(message);
  }
  return response.json();
}

// Create a new plan (POST /plans). Returns the parsed JSON ({ uri, ... }) on
// success; throws an Error whose message is the server's reason on failure.
export async function createPlan(createUrl, payload) {
  const response = await fetch(createUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let message = 'Something went wrong while saving. Please try again.';
    try {
      const errors = await response.json();
      const parts = Object.entries(errors).map(([field, msgs]) => {
        const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
        return field === 'base' ? text : `${field} ${text}`;
      });
      if (parts.length) message = parts.join('. ');
    } catch (_) { /* non-JSON error body */ }
    throw new Error(message);
  }
  return response.json();
}

// LocalStorage draft — same key scheme as v1 so a draft survives across versions.
export function draftKey(username, slug) { return `plans.${username}.${slug}`; }

export function saveDraft(username, slug, content) {
  try { localStorage.setItem(draftKey(username, slug), JSON.stringify(content)); } catch (_) { /* quota */ }
}
export function loadDraft(username, slug) {
  try { const v = localStorage.getItem(draftKey(username, slug)); return v ? JSON.parse(v) : null; } catch (_) { return null; }
}
export function clearDraft(username, slug) {
  try { localStorage.removeItem(draftKey(username, slug)); } catch (_) { /* ignore */ }
}
